<script lang="ts">
  import type { Permission } from '@clinic/contracts';
  import {
    Barcode,
    Boxes,
    Building2,
    CalendarDays,
    ChartColumn,
    ChartNoAxesColumn,
    CircleHelp,
    Ellipsis,
    HandCoins,
    History,
    House,
    KeyRound,
    ListChecks,
    LogOut,
    MessageSquareText,
    Palette,
    PanelRightClose,
    PanelRightOpen,
    Search,
    Settings,
    UserRound,
    Users,
  } from '@lucide/svelte';
  import { useQueryClient } from '@tanstack/svelte-query';
  import { type Component, type Snippet, untrack } from 'svelte';
  import ChangePasswordForm from '../features/auth/components/ChangePasswordForm.svelte';
  import { auth, createMeQuery } from '../shared/auth/auth.svelte';
  import { useOrg } from '../shared/auth/org.svelte';
  import { welcomeReplay } from '../features/welcome';
  import { usePendingCosts } from '../features/inventory';
  import { toPersianDigits } from '@clinic/money-date';
  import { fa } from '../shared/i18n/fa';
  import { cn } from '../shared/lib/cn';
  import { liveUpdates } from '../shared/live/live.svelte';
  import NavLink from '../shared/router/NavLink.svelte';
  import { location, navigate } from '../shared/router/router.svelte';
  import { rail, theme } from '../shared/theme/theme.svelte';
  import { AppIcon, DetailPanel, SectionTag } from '../shared/ui';
  import CommandPalette from './CommandPalette.svelte';
  import { paletteShortcut } from './palette.svelte';
  import { preloadOrgPages } from './routes';
  import AppearancePanel from './AppearancePanel.svelte';
  import ThemeSwitch from './ThemeSwitch.svelte';

  interface NavItem {
    to: string;
    label: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Icon: Component<any>;
    /** One permission, or any of several. */
    permission?: Permission | readonly Permission[];
    end?: boolean;
    /** Shown in the mobile dock; the rest sit behind «بیشتر». */
    primary?: boolean;
    /** Rail group: the day's work, the clinic's books and stock, administration. */
    group: 'work' | 'office' | 'admin';
    /** Something waiting there, said beside the name (e.g. goods without a purchase price). */
    badge?: { count: number; label: string };
  }

  /**
   * Two layouts: a side rail on desktop (≥1024px) that folds down to its icons, grouped into the
   * day's work, the office and administration, and a bottom dock on mobile. Items are hidden without permission; the
   * server enforces it anyway. Nothing the member may open is left out: what is not in the dock is
   * behind «بیشتر».
   */
  let { children }: { children: Snippet } = $props();

  const org = useOrg();
  const queryClient = useQueryClient();
  const me = createMeQuery();
  const multipleOrgs = $derived((me.data?.memberships.length ?? 0) > 1);
  // The member is known: their own look returns (ADR-060), whoever used this device before.
  $effect(() => {
    const id = me.data?.user.id;
    if (id) untrack(() => theme.bindUser(id));
  });
  const isClinic = $derived(org.membership.organization.type === 'clinic');
  // Staff of the laser section only (ADR-036) work in «بخش لیزر»: the same pages, laser data only.
  const laserOnly = $derived(isClinic && org.sections.desk.length === 1 && org.sections.desk[0] === 'laser');
  let paletteOpen = $state(false);
  let moreOpen = $state(false);
  // The sheet belongs to the page it was opened on: any change of page closes it, including the
  // back button of a browser that goes back in history instead of closing the open sheet.
  $effect(() => {
    void location.pathname;
    void location.search;
    untrack(() => (moreOpen = false));
  });
  let passwordOpen = $state(false);
  let appearanceOpen = $state(false);
  const live = liveUpdates(
    () => org.orgId,
    () => isClinic,
  );
  paletteShortcut(() => (paletteOpen = !paletteOpen));
  // The pages' code arrives in the background, so opening a page does not wait for the network.
  $effect(() => preloadOrgPages((p) => untrack(() => org.can(p))));

  const base = $derived(`/o/${org.orgId}`);
  // Goods received without a purchase price, for whoever gives prices (ADR-053).
  const pendingCosts = usePendingCosts(org.orgId, () => isClinic && org.can('inventory.cost'));
  const costBadge = $derived(
    pendingCosts.data && pendingCosts.data.total > 0
      ? { count: pendingCosts.data.total, label: fa.inventory.costs.badge(toPersianDigits(pendingCosts.data.total)) }
      : undefined,
  );
  const clinicItems = $derived<NavItem[]>(
    isClinic
      ? [
          { to: `${base}/calendar`, label: fa.nav.calendar, Icon: CalendarDays, permission: 'appointments.read', primary: true, group: 'work' },
          { to: `${base}/patients`, label: fa.nav.patients, Icon: UserRound, permission: 'patients.read', primary: true, group: 'work' },
          // The desk sends the queue from the phone every day: in the dock, not under «More» (docs/admin-today).
          { to: `${base}/sms`, label: fa.nav.sms, Icon: MessageSquareText, permission: 'sms.send', primary: true, group: 'office' },
          // The desk without the books gets only who owes (the same page, one tab).
          org.can('finance.read')
            ? { to: `${base}/finance`, label: fa.nav.finance, Icon: ChartNoAxesColumn, permission: 'finance.read', group: 'office' }
            : { to: `${base}/finance`, label: fa.nav.receivables, Icon: HandCoins, permission: 'balances.read', group: 'office' },
          { to: `${base}/inventory`, label: fa.nav.inventory, Icon: Boxes, permission: 'inventory.read', group: 'office', badge: costBadge },
          {
            to: `${base}/settings`,
            label: fa.nav.settings,
            Icon: Settings,
            permission: ['settings.manage', 'practitioner_settings.manage_own'],
            group: 'admin',
          },
        ]
      : [],
  );
  const items = $derived(
    [
      { to: base, label: fa.org.home, Icon: House, end: true, primary: true, group: 'work' } as NavItem,
      ...clinicItems,
      { to: `${base}/clinics`, label: fa.nav.clinics, Icon: Building2, permission: 'clinics.read', primary: true, group: 'work' } as NavItem,
      { to: `${base}/gtins`, label: fa.nav.gtins, Icon: Barcode, permission: 'catalog.review', group: 'work' } as NavItem,
      { to: `${base}/default-items`, label: fa.nav.defaultItems, Icon: ListChecks, permission: 'catalog.review', group: 'work' } as NavItem,
      { to: `${base}/insights`, label: fa.nav.insights, Icon: ChartColumn, permission: 'insights.read', group: 'work' } as NavItem,
      { to: `${base}/members`, label: fa.members.title, Icon: Users, permission: 'members.read', primary: !isClinic, group: 'admin' } as NavItem,
      { to: `${base}/audit`, label: fa.audit.title, Icon: History, permission: 'audit.read', primary: !isClinic, group: 'admin' } as NavItem,
    ].filter((i) => !i.permission || (typeof i.permission === 'string' ? [i.permission] : i.permission).some((p) => org.can(p))),
  );
  const groups = $derived(
    (['work', 'office', 'admin'] as const).map((g) => items.filter((i) => i.group === g)).filter((g) => g.length > 0),
  );
  const dock = $derived(items.filter((i) => i.primary));
  const overflow = $derived(items.filter((i) => !i.primary));

  async function logout() {
    await auth.signOut(queryClient);
    navigate('/login', { replace: true });
  }

  // The active item rises from the rail and gets an edge on the reading side (start = right in RTL)
  // as well as the weight, so the current page never relies on colour alone.
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'relative flex min-h-10 items-center gap-3 rounded-md px-3 text-sm transition-[background-color,color,box-shadow] duration-[var(--dur-base)]',
      isActive
        ? 'bg-sidebar-active font-semibold text-sidebar-active-ink shadow-raised-sm before:absolute before:inset-y-2.5 before:start-0 before:w-[3px] before:rounded-full before:bg-sidebar-active-ink'
        : 'text-text-2 hover:bg-surface-2 hover:text-text active:shadow-sunken',
    );
  const iconButton =
    'flex size-9 shrink-0 items-center justify-center rounded-md text-text-3 transition-colors duration-[var(--dur-fast)] hover:bg-surface-2 hover:text-text active:shadow-sunken';
  const headerButton = 'flex size-11 items-center justify-center rounded-md text-text-2 hover:bg-surface-2 active:shadow-sunken';
</script>

<div class="min-h-dvh lg:flex">
  <aside
    class={cn(
      'rail hidden shrink-0 flex-col gap-1 px-3 py-4 shadow-raised transition-[width] duration-[var(--dur-base)] ease-[var(--ease-out)] lg:sticky lg:top-0 lg:z-10 lg:flex lg:h-dvh',
      rail.collapsed ? 'w-[76px]' : 'w-[248px]',
    )}
  >
    <div class={cn('mb-3 flex items-center gap-2.5 px-1', rail.collapsed && 'flex-col')}>
      <AppIcon size="sm" />
      <div class={cn('min-w-0 flex-1', rail.collapsed && 'sr-only')}>
        <p class="truncate text-sm font-bold leading-tight">{org.membership.organization.name}</p>
        <p class="flex items-center gap-1.5 text-xs text-text-3">
          {#if laserOnly}{fa.sections.laserPanel}{/if}{#if isClinic}<span
              class={cn('size-1.5 rounded-full', live.status === 'live' ? 'bg-success' : 'bg-border-strong')}
              title={fa.calendar.live}
              aria-hidden="true"
            ></span>{/if}
        </p>
      </div>
      <button
        type="button"
        onclick={() => rail.toggle()}
        class={iconButton}
        aria-expanded={!rail.collapsed}
        aria-label={rail.collapsed ? fa.theme.railExpand : fa.theme.railCollapse}
        title={rail.collapsed ? fa.theme.railExpand : fa.theme.railCollapse}
      >
        {#if rail.collapsed}<PanelRightOpen class="size-4" aria-hidden="true" />{:else}<PanelRightClose class="size-4" aria-hidden="true" />{/if}
      </button>
    </div>
    <div class={cn('mb-3 flex items-center gap-1', rail.collapsed && 'flex-col')}>
      {#if isClinic}
        <button
          type="button"
          onclick={() => (paletteOpen = true)}
          aria-label={fa.palette.open}
          title={rail.collapsed ? fa.palette.open : undefined}
          class={cn(
            'flex min-h-9 items-center gap-1.5 rounded-md border border-border bg-surface-sunken text-sm text-text-3 shadow-sunken transition-colors duration-[var(--dur-fast)] hover:border-border-strong',
            rail.collapsed ? 'size-9 shrink-0 justify-center' : 'min-w-0 flex-1 px-2',
          )}
        >
          <Search class="size-4" aria-hidden="true" />
          {#if !rail.collapsed}
            <span class="min-w-0 flex-1 truncate text-start">{fa.app.search}</span>
            <!-- One line always; the rail is wide enough for the word beside the two appearance buttons. -->
            <kbd class="shrink-0 whitespace-nowrap rounded border border-border bg-surface px-1 text-[11px] text-text-4" dir="ltr">{fa.palette.shortcut}</kbd>
          {/if}
        </button>
      {/if}
      <ThemeSwitch class={iconButton} />
      <button type="button" onclick={() => (appearanceOpen = true)} class={iconButton} aria-label={fa.theme.panel} title={fa.theme.panel}>
        <Palette class="size-4" aria-hidden="true" />
      </button>
    </div>
    <nav aria-label={fa.app.name} class="-m-1 flex min-h-0 flex-col overflow-y-auto overflow-x-hidden p-1">
      {#each groups as group, index (group[0]?.group)}
        <div class={cn('flex flex-col gap-1', index > 0 && 'mt-2 border-t border-hairline pt-2')}>
          {#each group as item (item.to)}
            <NavLink to={item.to} end={item.end} class={linkClass}
              ><span class="flex" title={rail.collapsed ? item.label : undefined}><item.Icon class="size-4 shrink-0" aria-hidden="true" /></span><span
                class={rail.collapsed ? 'sr-only' : 'min-w-0 truncate'}>{item.label}</span
              >{#if item.badge}<span
                  class={cn(
                    'rounded-full border border-warn-line bg-warn-soft px-1.5 text-xs font-bold tabular-nums text-warn',
                    rail.collapsed ? 'absolute end-0.5 top-0.5 px-1 text-[10px]' : 'ms-auto',
                  )}
                  title={item.badge.label}><span aria-hidden="true">{toPersianDigits(item.badge.count)}</span><span class="sr-only">{item.badge.label}</span></span
                >{/if}</NavLink
            >
          {/each}
        </div>
      {/each}
    </nav>
    <div class="mt-auto flex flex-col gap-1 border-t border-hairline pt-3">
      {#if multipleOrgs}
        <NavLink to="/orgs" class={linkClass}
          ><Building2 class="size-4 shrink-0" aria-hidden="true" /><span class={rail.collapsed ? 'sr-only' : ''}>{fa.org.switch}</span></NavLink
        >
      {/if}
      <div class={cn('flex items-center gap-1 px-1', rail.collapsed && 'flex-col')}>
        <span class={cn('min-w-0 flex-1 truncate text-sm text-text-2', rail.collapsed && 'sr-only')}>{me.data?.user.displayName ?? ''}</span>
        {#if isClinic}
          <button
            type="button"
            onclick={() => welcomeReplay.open()}
            class={iconButton}
            aria-label={fa.welcomeTour.menu}
            title={fa.welcomeTour.menu}
          >
            <CircleHelp class="size-4" aria-hidden="true" />
          </button>
        {/if}
        <button
          type="button"
          onclick={() => (passwordOpen = true)}
          class={iconButton}
          aria-label={fa.password.changeTitle}
          title={fa.password.changeTitle}
        >
          <KeyRound class="size-4" aria-hidden="true" />
        </button>
        <button type="button" onclick={() => void logout()} class={iconButton} aria-label={fa.auth.logout} title={fa.auth.logout}>
          <LogOut class="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  </aside>

  <div class="min-w-0 flex-1">
    <header class="flex items-center justify-between gap-3 bg-surface px-4 py-2 shadow-raised-sm lg:hidden">
      <span class="flex min-w-0 items-center gap-2.5">
        <AppIcon size="sm" />
        <span class="truncate font-bold">{org.membership.organization.name}</span>
        {#if laserOnly}<SectionTag section="laser" />{/if}
      </span>
      <span class="flex shrink-0 items-center">
        <ThemeSwitch class={headerButton} iconClass="size-5" />
        <button type="button" onclick={() => (appearanceOpen = true)} class={headerButton} aria-label={fa.theme.panel}>
          <Palette class="size-5" aria-hidden="true" />
        </button>
        {#if isClinic}
          <button type="button" onclick={() => (paletteOpen = true)} class={headerButton} aria-label={fa.palette.open}>
            <Search class="size-5" aria-hidden="true" />
          </button>
        {/if}
      </span>
    </header>
    <main class="mx-auto w-full max-w-6xl px-4 pb-24 pt-5 lg:px-8 lg:pb-10 lg:pt-7">{@render children()}</main>
  </div>

  <nav
    aria-label={fa.app.name}
    class="rail fixed inset-x-0 bottom-0 z-40 flex justify-around rounded-t-xl px-1.5 pt-1.5 shadow-raised-lg lg:hidden"
    style="padding-bottom: max(0.25rem, env(safe-area-inset-bottom))"
  >
    {#each dock as item (item.to)}
      <NavLink
        to={item.to}
        end={item.end}
        class={({ isActive }) =>
          cn(
            'flex min-h-12 min-w-16 flex-col items-center justify-center gap-0.5 rounded-md px-2 text-[11px] transition-[background-color,color,box-shadow] duration-[var(--dur-base)]',
            isActive ? 'bg-sidebar-active font-semibold text-sidebar-active-ink shadow-raised-sm' : 'text-text-3 active:bg-surface-2',
          )}><item.Icon class="size-5" aria-hidden="true" />{item.label}</NavLink
      >
    {/each}
    <button
      type="button"
      onclick={() => (moreOpen = true)}
      class="flex min-h-12 min-w-16 flex-col items-center justify-center gap-0.5 rounded-md px-2 text-[11px] text-text-3"
      ><Ellipsis class="size-5" aria-hidden="true" />{fa.nav.more}</button
    >
  </nav>

  <DetailPanel open={moreOpen} onClose={() => (moreOpen = false)} title={fa.nav.more}>
    <div class="flex flex-col gap-1">
      {#each overflow as item (item.to)}
        <NavLink to={item.to} class={linkClass} onclick={() => (moreOpen = false)}
          ><item.Icon class="size-4" aria-hidden="true" />{item.label}</NavLink
        >
      {/each}
      {#if multipleOrgs}
        <NavLink to="/orgs" class={linkClass} onclick={() => (moreOpen = false)}
          ><Building2 class="size-4" aria-hidden="true" />{fa.org.switch}</NavLink
        >
      {/if}
      {#if isClinic}
        <button
          type="button"
          onclick={() => {
            moreOpen = false;
            welcomeReplay.open();
          }}
          class={linkClass({ isActive: false })}><CircleHelp class="size-4" aria-hidden="true" />{fa.welcomeTour.menu}</button
        >
      {/if}
      <button
        type="button"
        onclick={() => {
          moreOpen = false;
          passwordOpen = true;
        }}
        class={linkClass({ isActive: false })}><KeyRound class="size-4" aria-hidden="true" />{fa.password.changeTitle}</button
      >
      <button type="button" onclick={() => void logout()} class={linkClass({ isActive: false })}
        ><LogOut class="size-4" aria-hidden="true" />{fa.auth.logout}</button
      >
    </div>
  </DetailPanel>

  <DetailPanel open={passwordOpen} onClose={() => (passwordOpen = false)} title={fa.password.changeTitle}>
    {#if passwordOpen}<ChangePasswordForm />{/if}
  </DetailPanel>

  <AppearancePanel open={appearanceOpen} onClose={() => (appearanceOpen = false)} />

  {#if isClinic}<CommandPalette open={paletteOpen} onClose={() => (paletteOpen = false)} />{/if}
</div>
